import {
  computed,
  defineComponent,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  reactive,
  shallowRef,
  toRaw,
  unref,
  watch,
  watchEffect
} from 'vue';

import { classNames, supportsPassive } from '@components/utils';
import { useRender } from '@components/composable';

import Item from './Item';
import Filler from './Filler';
import ScrollBar from './ScrollBar';
import { useFrameWheel, useHeights, useMobileTouchMove, useOriginScroll, useScrollTo } from './features';

import type { RenderFunc, SharedConfig } from './interface';
import type { PropType, VNode, CSSProperties, Component } from 'vue';

const EMPTY_DATA: any[] = [];

const ScrollStyle: CSSProperties = {
  overflowY: 'auto',
  overflowAnchor: 'none'
};

type Key = string | number;
interface ListState {
  scrollTop: number;
  scrollMoving: boolean;
}

export type ScrollAlign = 'top' | 'bottom' | 'auto';
export type ScrollConfig =
  | {
      index: number;
      align?: ScrollAlign;
      offset?: number;
    }
  | {
      key: Key;
      align?: ScrollAlign;
      offset?: number;
    };
export type ScrollTo = (arg: number | ScrollConfig) => void;

export default defineComponent({
  name: 'VirtualList',
  inheritAttrs: false,
  props: {
    prefixCls: String as PropType<string>,
    data: Array as PropType<any[]>,
    height: {
      type: Number as PropType<number>,
      default: 0
    },
    itemHeight: {
      type: Number as PropType<number>,
      default: 0
    },
    fullHeight: { type: Boolean as PropType<boolean>, default: undefined },
    itemKey: {
      type: [String, Number, Function] as PropType<Key | ((item: Record<string, any>) => Key)>,
      required: true
    },
    component: {
      type: [String, Object] as PropType<string | Component>
    },
    /** Set `false` will always use real scroll instead of virtual one */
    virtual: { type: Boolean, default: undefined },
    children: Function,
    onScroll: Function,
    onMousedown: Function,
    onMouseEnter: Function,
    onVisibleChange: Function as PropType<(visibleList: any[], fullList: any[]) => void>
  },
  setup(props, { slots, attrs, expose }) {
    const useVirtual = computed(() => !!(props.virtual !== false && props.height && props.itemHeight));
    const inVirtual = computed(
      () => unref(useVirtual) && props.data && props.itemHeight * props.data.length > props.height
    );

    const state = reactive<ListState>({
      scrollTop: 0,
      scrollMoving: false
    });

    const data = computed(() => props.data || EMPTY_DATA);
    const mergedData = shallowRef<any[]>([]);

    watch(
      data,
      () => {
        mergedData.value = toRaw(unref(data)).slice();
      },
      { immediate: true }
    );

    const itemKey = shallowRef((_item: Record<string, any>) => undefined);

    watch(
      () => props.itemKey,
      (value) => {
        if (typeof value === 'function') {
          // @ts-ignore
          itemKey.value = value;
        } else {
          itemKey.value = (item) => item?.[value];
        }
      },
      { immediate: true }
    );

    const componentRef = shallowRef<HTMLDivElement>();
    const fillerInnerRef = shallowRef<HTMLDivElement>();
    const scrollBarRef = shallowRef<any>();

    // =============================== Item Key ===============================
    const getKey = (item: Record<string, any>): any => unref(itemKey)(item);

    const sharedConfig = { getKey };
    // =============================== Item Key ===============================

    // ================================ Height ================================
    const { setInstance, collectHeight, heights, updatedMark } = useHeights(mergedData, getKey, null, null);

    const calRes = reactive<{
      scrollHeight?: number;
      start?: number;
      end?: number;
      offset?: number;
    }>({
      scrollHeight: undefined,
      start: 0,
      end: 0,
      offset: undefined
    });

    const offsetHeight = shallowRef(0);
    onMounted(() => {
      nextTick(() => {
        offsetHeight.value = unref(fillerInnerRef)?.offsetHeight || 0;
      });
    });
    onUpdated(() => {
      nextTick(() => {
        offsetHeight.value = unref(fillerInnerRef)?.offsetHeight || 0;
      });
    });

    watch(
      [useVirtual, mergedData],
      () => {
        if (!unref(useVirtual)) {
          Object.assign(calRes, {
            scrollHeight: undefined,
            start: 0,
            end: unref(mergedData).length - 1,
            offset: undefined
          });
        }
      },
      { immediate: true }
    );

    watch(
      [useVirtual, mergedData, offsetHeight, inVirtual],
      () => {
        // Always use virtual scroll bar in avoid shaking
        if (unref(useVirtual) && !unref(inVirtual)) {
          Object.assign(calRes, {
            scrollHeight: unref(offsetHeight),
            start: 0,
            end: unref(mergedData).length - 1,
            offset: undefined
          });
        }

        if (unref(componentRef)) {
          state.scrollTop = unref(componentRef)!.scrollTop;
        }
      },
      { immediate: true }
    );

    watch(
      [inVirtual, useVirtual, () => state.scrollTop, mergedData, updatedMark, () => props.height, offsetHeight],
      () => {
        if (!unref(useVirtual) || !unref(inVirtual)) return;

        let itemTop = 0;
        let startIndex: number | undefined;
        let startOffset: number | undefined;
        let endIndex: number | undefined;

        const dataLength = unref(mergedData).length;
        const data = unref(mergedData);
        const scrollTop = state.scrollTop;
        const { height, itemHeight } = props;
        const scrollTopHeight = scrollTop + height;

        for (let i = 0; i < dataLength; i++) {
          const item = data[i];
          const key = getKey(item);

          let cacheHeight = heights.get(key);
          if (cacheHeight === undefined) {
            cacheHeight = itemHeight;
          }

          const currentItemBottom = itemTop + cacheHeight;

          if (startIndex === undefined && currentItemBottom >= scrollTop) {
            startIndex = i;
            startOffset = itemTop;
          }

          if (endIndex === undefined && currentItemBottom > scrollTopHeight) {
            endIndex = i;
          }

          itemTop = currentItemBottom;
        }

        if (startIndex === undefined) {
          startIndex = 0;
          startOffset = 0;
          endIndex = Math.ceil(height / itemHeight);
        }

        if (endIndex === undefined) {
          endIndex = dataLength - 1;
        }

        endIndex = Math.min(endIndex + 1, dataLength);

        Object.assign(calRes, {
          scrollHeight: itemTop,
          start: startIndex,
          end: endIndex,
          offset: startOffset
        });
      },
      { immediate: true }
    );
    // ================================ Height ================================

    // =============================== In Range ===============================
    const maxScrollHeight = computed(() => calRes.scrollHeight! - props.height!);

    function keepInRange(newScrollTop: number) {
      let newTop = newScrollTop;
      if (!Number.isNaN(unref(maxScrollHeight))) {
        newTop = Math.min(newTop, unref(maxScrollHeight));
      }
      newTop = Math.max(newTop, 0);
      return newTop;
    }

    const isScrollAtTop = computed(() => state.scrollTop <= 0);
    const isScrollAtBottom = computed(() => state.scrollTop >= unref(maxScrollHeight));

    const originScroll = useOriginScroll(isScrollAtTop, isScrollAtBottom);
    // =============================== In Range ===============================

    // ================================ Scroll ================================
    function syncScrollTop(newTop: number | ((prev: number) => number)) {
      let value: number;

      if (typeof newTop === 'function') {
        value = newTop(state.scrollTop);
      } else {
        value = newTop;
      }

      const alignedTop = keepInRange(value);

      if (unref(componentRef)) {
        unref(componentRef)!.scrollTop = alignedTop;
      }
      state.scrollTop = alignedTop;
    }

    function onScrollBar(newScrollTop: number) {
      syncScrollTop(newScrollTop);
    }

    // When data size reduce. It may trigger native scroll event back to fit scroll position
    function onFallbackScroll(event: UIEvent) {
      const { scrollTop: newScrollTop } = event.currentTarget as Element;

      if (newScrollTop !== state.scrollTop) {
        syncScrollTop(newScrollTop);
      }

      // Trigger origin onScroll event
      props.onScroll?.(event);
    }

    // Since this added in global, should use ref to keep updated
    const { onWheel, onFireFoxScroll } = useFrameWheel(useVirtual, isScrollAtTop, isScrollAtBottom, (offsetY) => {
      syncScrollTop((top) => {
        return top + offsetY;
      });
    });

    // Mobile touch move
    useMobileTouchMove(useVirtual, componentRef, (deltaY, smoothOffset) => {
      if (originScroll(deltaY, smoothOffset)) return false;

      onWheel({ preventDefault() {}, deltaY } as WheelEvent);
      return true;
    });

    // Firefox only
    function onMozMousePixelScroll(event: MouseEvent) {
      if (unref(useVirtual)) {
        event.preventDefault();
      }
    }

    const removeEventListener = () => {
      if (unref(componentRef)) {
        unref(componentRef)?.removeEventListener(
          'wheel',
          onWheel,
          supportsPassive ? ({ passive: false } as EventListenerOptions) : false
        );

        unref(componentRef)?.removeEventListener('DOMMouseScroll', onFireFoxScroll as any);
        unref(componentRef)?.removeEventListener('MozMousePixelScroll', onMozMousePixelScroll as any);
      }
    };

    watchEffect(() => {
      nextTick(() => {
        if (unref(componentRef)) {
          removeEventListener();
          unref(componentRef)?.addEventListener('wheel', onWheel, supportsPassive ? { passive: false } : false);
          unref(componentRef)?.addEventListener('DOMMouseScroll', onFireFoxScroll as any);
          unref(componentRef)?.addEventListener('MozMousePixelScroll', onMozMousePixelScroll as any);
        }
      });
    });

    onBeforeUnmount(() => {
      removeEventListener();
    });
    // ================================ Scroll ================================

    // ================================= Ref ==================================
    const scrollTo = useScrollTo(componentRef, mergedData, heights, props, getKey, collectHeight, syncScrollTop, () => {
      unref(scrollBarRef)?.syncScrollBar();
    });

    expose({
      scrollTo
    });

    const componentStyle = computed(() => {
      let css: CSSProperties | null = null;
      if (props.height) {
        css = { [props.fullHeight ? 'height' : 'maxHeight']: props.height + 'px', ...ScrollStyle };

        if (useVirtual.value) {
          css!.overflowY = 'hidden';

          if (state.scrollMoving) {
            css!.pointerEvents = 'none';
          }
        }
      }
      return css;
    });
    // ================================= Ref ==================================

    // ================================ Effect ================================
    /** We need told outside that some list not rendered */
    watch(
      [() => calRes.start, () => calRes.end, mergedData],
      () => {
        if (props.onVisibleChange) {
          const renderList = unref(mergedData).slice(calRes.start, calRes.end! + 1);

          props.onVisibleChange(renderList, unref(mergedData));
        }
      },
      { flush: 'post' }
    );

    useRender(() => {
      const {
        prefixCls = 'cds-virtual-list',
        height,
        itemHeight,
        fullHeight,
        data,
        itemKey,
        virtual,
        component: Component = 'div',
        onScroll,
        children = slots.default,
        ...restProps
      } = props;
      const { style, class: className } = attrs as { style: CSSProperties; class: string };
      const mergedClassName = classNames(prefixCls, className);
      const { scrollTop } = state;
      const { scrollHeight, offset, start, end } = calRes;

      return h('div', { style: { ...style, position: 'relative' }, class: mergedClassName, ...restProps }, [
        h(
          Component as VNode,
          {
            ref: componentRef,
            class: `${prefixCls}__holder`,
            style: unref(componentStyle),
            onScroll: onFallbackScroll
          },
          Filler(
            { ref: fillerInnerRef, prefixCls, height: scrollHeight!, offset, onInnerResize: collectHeight },
            {
              slots: {
                default: () =>
                  renderChildren(unref(mergedData), start!, end!, setInstance, children as any, sharedConfig)
              },
              attrs: {},
              emit: () => undefined
            }
          )
        ),

        unref(useVirtual) &&
          h(ScrollBar, {
            ref: scrollBarRef,
            prefixCls,
            scrollTop,
            height,
            scrollHeight,
            count: unref(mergedData).length,
            onScroll: onScrollBar,
            onStarMove: () => {
              state.scrollMoving = true;
            },
            onStopMove: () => {
              state.scrollMoving = false;
            }
          })
      ]);
    });
  }
});

function renderChildren<T>(
  list: T[],
  startIndex: number,
  endIndex: number,
  setNodeRef: (item: T, element: HTMLElement) => void,
  renderFunc: RenderFunc<T>,
  { getKey }: SharedConfig<T>
) {
  return list.slice(startIndex, endIndex + 1).map((item, index) => {
    const elementIndex = startIndex + index;
    const node = renderFunc(item, elementIndex, {});
    const key = getKey(item);

    return h(Item, { key, setRef: (el) => setNodeRef(item, el) }, { default: () => node });
  });
}
